import type { Booking } from "@/logic/api";
import {
  getBookingCardValues,
  type BookingCardValues,
} from "@/components/lists/listItems/BookingListItem/getBookingCardValues";

export type BookingKey = Booking["uuid"];

export interface LiveBookingChanges {
  revision: number;
  addedUuids: ReadonlySet<BookingKey>;
  updatedUuids: ReadonlySet<BookingKey>;
  removedUuids: ReadonlySet<BookingKey>;
  orderChanged: boolean;
}

const cardValuesChanged = (before: Booking, after: Booking): boolean => {
  const beforeValues = getBookingCardValues(before);
  const afterValues = getBookingCardValues(after);
  for (const key of Object.keys(beforeValues) as (keyof BookingCardValues)[]) {
    if (beforeValues[key] !== afterValues[key]) return true;
  }
  return false;
};

// A live update replaces the whole list. This diff tells the cards which ones
// arrived, left, moved, or changed on screen.
export const getLiveBookingChanges = (
  before: ReadonlyArray<Booking>,
  after: ReadonlyArray<Booking>,
): Omit<LiveBookingChanges, "revision"> => {
  const addedUuids = new Set<BookingKey>();
  const updatedUuids = new Set<BookingKey>();
  const removedUuids = new Set<BookingKey>();
  if (before === after) {
    return { addedUuids, updatedUuids, removedUuids, orderChanged: false };
  }

  const previousBookings = new Map<BookingKey, { booking: Booking; index: number }>();
  before.forEach((booking, index) => {
    previousBookings.set(booking.uuid, { booking, index });
  });

  let orderChanged = false;
  const nextUuids = new Set<BookingKey>();
  after.forEach((booking, index) => {
    nextUuids.add(booking.uuid);
    const previous = previousBookings.get(booking.uuid);
    if (previous == null) {
      addedUuids.add(booking.uuid);
    } else {
      if (previous.index !== index) orderChanged = true;
      if (
        previous.booking !== booking &&
        cardValuesChanged(previous.booking, booking)
      ) {
        updatedUuids.add(booking.uuid);
      }
    }
  });

  for (const booking of before) {
    if (!nextUuids.has(booking.uuid)) removedUuids.add(booking.uuid);
  }

  return { addedUuids, updatedUuids, removedUuids, orderChanged };
};
